import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';


const OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'greater_than', label: 'is greater than' },
  { value: 'less_than', label: 'is less than' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
  { value: 'is_checked', label: 'is checked' },
  { value: 'is_not_checked', label: 'is not checked' },
];

const FIELD_ACTIONS = [
  { value: 'show', label: 'Show' },
  { value: 'hide', label: 'Hide' },
  { value: 'require', label: 'Require' },
  { value: 'unrequire', label: 'Make optional' },
  { value: 'enable', label: 'Enable' },
  { value: 'disable', label: 'Disable' },
];

const PAGE_ACTIONS = [
  { value: 'jump_to_page', label: 'Jump to page' },
  { value: 'skip_page', label: 'Skip page' },
];

const MATCH_TYPES = [
  { value: 'all', label: 'ALL conditions are met' },
  { value: 'any', label: 'ANY condition is met' },
];

const noValueOperators = ['is_empty', 'is_not_empty', 'is_checked', 'is_not_checked'];

const emptyCondition = () => ({ id: Date.now(), fieldId: '', operator: 'equals', value: '' });
const emptyRule = () => ({
  id: Date.now(),
  matchType: 'all',
  conditions: [emptyCondition()],
  action: 'show',
  targetFieldId: '',
  targetPageId: '',
  targetType: 'field', // 'field' | 'page'
  enabled: true,
});

const ConditionalLogicPanel = ({ formElements = [], logicRules = [], onUpdateRules, multiStepConfig = null }) => {
  const [expandedRule, setExpandedRule] = useState(null);

  const multiStepEnabled = multiStepConfig?.enabled || false;
  const pages = multiStepConfig?.steps || [];

  const fieldOptions = formElements?.filter(el => el?.type !== 'button' && el?.type !== 'content' && el?.type !== 'media' && el?.type !== 'layout')?.map(el => ({ value: el?.id, label: el?.label || el?.name || el?.id }));

  const allFieldOptions = formElements?.filter(el => el?.type !== 'layout' && el?.type !== 'media')?.map(el => ({ value: el?.id, label: el?.label || el?.name || el?.id }));

  const pageOptions = pages?.map((p, idx) => ({ value: p?.id, label: `Page ${idx + 1}: ${p?.title || `Page ${idx + 1}`}` }));

  // Combined actions based on whether multi-step is enabled
  const getActionsForRule = (rule) => {
    if (multiStepEnabled && rule?.targetType === 'page') {
      return PAGE_ACTIONS;
    }
    return FIELD_ACTIONS;
  };

  const addRule = () => {
    const newRule = emptyRule();
    const updated = [...logicRules, newRule];
    onUpdateRules?.(updated);
    setExpandedRule(newRule?.id);
  };

  const removeRule = (ruleId) => {
    onUpdateRules?.(logicRules?.filter(r => r?.id !== ruleId));
    if (expandedRule === ruleId) setExpandedRule(null);
  };

  const updateRule = (ruleId, changes) => {
    onUpdateRules?.(logicRules?.map(r => r?.id === ruleId ? { ...r, ...changes } : r));
  };

  const addCondition = (ruleId) => {
    onUpdateRules?.(logicRules?.map(r =>
      r?.id === ruleId ? { ...r, conditions: [...r?.conditions, emptyCondition()] } : r
    ));
  };

  const removeCondition = (ruleId, condId) => {
    onUpdateRules?.(logicRules?.map(r =>
      r?.id === ruleId ? { ...r, conditions: r?.conditions?.filter(c => c?.id !== condId) } : r
    ));
  };

  const updateCondition = (ruleId, condId, changes) => {
    onUpdateRules?.(logicRules?.map(r =>
      r?.id === ruleId
        ? { ...r, conditions: r?.conditions?.map(c => c?.id === condId ? { ...c, ...changes } : c) }
        : r
    ));
  };

  const getFieldLabel = (fieldId) => {
    const f = formElements?.find(el => el?.id === fieldId);
    return f ? (f?.label || f?.name || fieldId) : fieldId;
  };

  const getPageLabel = (pageId) => {
    const p = pages?.find(pg => pg?.id === pageId);
    if (!p) return pageId;
    const idx = pages?.indexOf(p);
    return `Page ${idx + 1}: ${p?.title || `Page ${idx + 1}`}`;
  };

  const getRuleSummary = (rule) => {
    if (!rule?.conditions?.[0]?.fieldId) return 'Incomplete rule — click to configure';
    const condCount = rule?.conditions?.length;
    const isPageTarget = rule?.targetType === 'page';
    const actionOptions = isPageTarget ? PAGE_ACTIONS : FIELD_ACTIONS;
    const actionLabel = actionOptions?.find(a => a?.value === rule?.action)?.label || rule?.action;
    const targetLabel = isPageTarget
      ? (rule?.targetPageId ? getPageLabel(rule?.targetPageId) : '?')
      : (rule?.targetFieldId ? getFieldLabel(rule?.targetFieldId) : '?');
    if (!targetLabel || targetLabel === '?') return 'Incomplete rule — click to configure';
    const firstCond = rule?.conditions?.[0];
    const opLabel = OPERATORS?.find(o => o?.value === firstCond?.operator)?.label || firstCond?.operator;
    const srcLabel = getFieldLabel(firstCond?.fieldId);
    const extra = condCount > 1 ? ` + ${condCount - 1} more` : '';
    return `If [${srcLabel}] ${opLabel}${firstCond?.value ? ` "${firstCond?.value}"` : ''}${extra} → ${actionLabel} [${targetLabel}]`;
  };

  return (
    <div className="space-y-4">
      {/* Header info */}
      <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg flex items-start gap-3">
        <Icon name="GitBranch" size={18} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-foreground">Conditional Logic Engine</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Create rules to show, hide, or require fields based on user input.
            {multiStepEnabled && ' You can also jump between pages.'}
          </p>
        </div>
      </div>

      {/* Multi-step notice */}
      {multiStepEnabled && pages?.length > 0 && (
        <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <Icon name="Layers" size={14} color="#3b82f6" className="mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-700">
            Multi-step is active ({pages?.length} pages). Rules can now target pages to control navigation flow.
          </p>
        </div>
      )}

      {/* Rules list */}
      {logicRules?.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-border rounded-lg">
          <Icon name="GitBranch" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No logic rules yet</p>
          <p className="text-xs text-muted-foreground mt-1">Add a rule to make your form dynamic</p>
        </div>
      ) : (
        <div className="space-y-2">
          {logicRules?.map((rule, idx) => (
            <div key={rule?.id} className="border border-border rounded-lg overflow-hidden bg-card">
              {/* Rule header */}
              <div
                className="flex items-center gap-2 p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setExpandedRule(expandedRule === rule?.id ? null : rule?.id)}
              >
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${rule?.enabled ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                <span className="text-xs font-medium text-muted-foreground flex-shrink-0">Rule {idx + 1}</span>
                {rule?.targetType === 'page' && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded flex-shrink-0">Page</span>
                )}
                <span className="text-xs text-foreground flex-1 truncate">{getRuleSummary(rule)}</span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={(e) => { e?.stopPropagation(); updateRule(rule?.id, { enabled: !rule?.enabled }); }}
                    className={`p-1 rounded transition-colors ${rule?.enabled ? 'text-green-600 hover:bg-green-50' : 'text-muted-foreground hover:bg-muted'}`}
                    title={rule?.enabled ? 'Disable rule' : 'Enable rule'}
                  >
                    <Icon name={rule?.enabled ? 'ToggleRight' : 'ToggleLeft'} size={16} />
                  </button>
                  <button
                    onClick={(e) => { e?.stopPropagation(); removeRule(rule?.id); }}
                    className="p-1 rounded text-destructive hover:bg-destructive/10 transition-colors"
                    title="Delete rule"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                  <Icon name={expandedRule === rule?.id ? 'ChevronUp' : 'ChevronDown'} size={14} color="var(--color-muted-foreground)" />
                </div>
              </div>

              {/* Expanded rule editor */}
              {expandedRule === rule?.id && (
                <div className="border-t border-border p-3 space-y-4 bg-muted/10">
                  {/* Match type */}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Match type</label>
                    <div className="flex gap-2">
                      {MATCH_TYPES?.map(mt => (
                        <button
                          key={mt?.value}
                          onClick={() => updateRule(rule?.id, { matchType: mt?.value })}
                          className={`flex-1 py-1.5 px-2 text-xs rounded-md border transition-colors ${
                            rule?.matchType === mt?.value
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background border-border text-foreground hover:bg-muted'
                          }`}
                        >
                          {mt?.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Conditions */}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-2 block">
                      IF {rule?.matchType === 'all' ? 'ALL' : 'ANY'} of these conditions:
                    </label>
                    <div className="space-y-2">
                      {rule?.conditions?.map((cond, ci) => (
                        <div key={cond?.id} className="bg-background border border-border rounded-md p-2 space-y-2">
                          {ci > 0 && (
                            <div className="text-xs font-medium text-primary text-center">
                              {rule?.matchType === 'all' ? 'AND' : 'OR'}
                            </div>
                          )}
                          <div className="flex gap-1.5 items-start">
                            <div className="flex-1 min-w-0">
                              <Select
                                options={[{ value: '', label: 'Select field...' }, ...fieldOptions]}
                                value={cond?.fieldId}
                                onChange={(val) => updateCondition(rule?.id, cond?.id, { fieldId: val })}
                              />
                            </div>
                            {rule?.conditions?.length > 1 && (
                              <button
                                onClick={() => removeCondition(rule?.id, cond?.id)}
                                className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors flex-shrink-0 mt-0.5"
                              >
                                <Icon name="X" size={12} />
                              </button>
                            )}
                          </div>
                          <Select
                            options={OPERATORS}
                            value={cond?.operator}
                            onChange={(val) => updateCondition(rule?.id, cond?.id, { operator: val })}
                          />
                          {!noValueOperators?.includes(cond?.operator) && (
                            <input
                              type="text"
                              value={cond?.value}
                              onChange={(e) => updateCondition(rule?.id, cond?.id, { value: e?.target?.value })}
                              placeholder="Enter value..."
                              className="w-full px-2.5 py-1.5 bg-background border border-input rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => addCondition(rule?.id)}
                      className="mt-2 flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
                    >
                      <Icon name="Plus" size={12} />
                      Add condition
                    </button>
                  </div>

                  {/* Action — target type selector when multi-step is on */}
                  <div className="pt-2 border-t border-border space-y-2">
                    <label className="text-xs font-medium text-muted-foreground block">THEN perform this action:</label>

                    {/* Target type toggle (only shown when multi-step enabled) */}
                    {multiStepEnabled && pages?.length > 0 && (
                      <div className="flex gap-2 mb-2">
                        <button
                          onClick={() => updateRule(rule?.id, { targetType: 'field', action: 'show', targetFieldId: '', targetPageId: '' })}
                          className={`flex-1 py-1.5 px-2 text-xs rounded-md border transition-colors flex items-center justify-center gap-1 ${
                            rule?.targetType !== 'page' ?'bg-primary text-primary-foreground border-primary' :'bg-background border-border text-foreground hover:bg-muted'
                          }`}
                        >
                          <Icon name="Type" size={11} />
                          Field action
                        </button>
                        <button
                          onClick={() => updateRule(rule?.id, { targetType: 'page', action: 'jump_to_page', targetFieldId: '', targetPageId: '' })}
                          className={`flex-1 py-1.5 px-2 text-xs rounded-md border transition-colors flex items-center justify-center gap-1 ${
                            rule?.targetType === 'page' ?'bg-primary text-primary-foreground border-primary' :'bg-background border-border text-foreground hover:bg-muted'
                          }`}
                        >
                          <Icon name="Layers" size={11} />
                          Page action
                        </button>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Select
                          options={getActionsForRule(rule)}
                          value={rule?.action}
                          onChange={(val) => updateRule(rule?.id, { action: val })}
                        />
                      </div>
                      <div className="flex-1">
                        {rule?.targetType === 'page' ? (
                          <Select
                            options={[{ value: '', label: 'Select page...' }, ...pageOptions]}
                            value={rule?.targetPageId || ''}
                            onChange={(val) => updateRule(rule?.id, { targetPageId: val })}
                          />
                        ) : (
                          <Select
                            options={[{ value: '', label: 'Select field...' }, ...allFieldOptions]}
                            value={rule?.targetFieldId}
                            onChange={(val) => updateRule(rule?.id, { targetFieldId: val })}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <Button
        variant="outline"
        size="sm"
        iconName="Plus"
        iconPosition="left"
        onClick={addRule}
        className="w-full"
      >
        Add Logic Rule
      </Button>
      {logicRules?.length > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {logicRules?.filter(r => r?.enabled)?.length} of {logicRules?.length} rule{logicRules?.length !== 1 ? 's' : ''} active
        </p>
      )}
    </div>
  );
};

export default ConditionalLogicPanel;
